/*
 * Minimal nostr client: NIP-19 npub decoding (bech32) and a NIP-01
 * one-shot relay query over plain WebSockets.
 */

const CHARSET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
const GEN = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];

/** Decode an npub1… string to a 64-char hex pubkey. Throws on anything invalid. */
export function decodeNpub(npub: string): string {
  const sep = npub.lastIndexOf("1");
  if (sep === -1 || npub.slice(0, sep) !== "npub") throw new Error("not an npub");
  const data = [...npub.slice(sep + 1)].map((ch) => CHARSET.indexOf(ch));
  if (data.length < 7 || data.includes(-1)) throw new Error("invalid bech32 data");

  let chk = 1;
  const mix = (v: number) => {
    const top = chk >> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ v;
    for (let i = 0; i < 5; i++) if ((top >> i) & 1) chk ^= GEN[i]!;
  };
  for (const ch of "npub") mix(ch.charCodeAt(0) >> 5);
  mix(0);
  for (const ch of "npub") mix(ch.charCodeAt(0) & 31);
  for (const d of data) mix(d);
  if (chk !== 1) throw new Error("bad bech32 checksum");

  // regroup the 5-bit words (minus the 6 checksum words) into bytes
  let acc = 0;
  let nbits = 0;
  const bytes: number[] = [];
  for (const w of data.slice(0, -6)) {
    acc = (acc << 5) | w;
    nbits += 5;
    while (nbits >= 8) {
      nbits -= 8;
      bytes.push((acc >> nbits) & 0xff);
    }
  }
  if (nbits >= 5 || acc & ((1 << nbits) - 1)) throw new Error("bad bech32 padding");
  if (bytes.length !== 32) throw new Error("npub must decode to 32 bytes");
  return bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export interface NostrEvent {
  id: string;
  pubkey: string;
  created_at: number;
  kind: number;
  tags: string[][];
  content: string;
  sig: string;
}

export interface Filter {
  kinds?: number[];
  authors?: string[];
  limit?: number;
}

/** Ask one relay for events matching the filter; resolve with the newest at EOSE. */
function queryRelay(url: string, filter: Filter, timeoutMs: number): Promise<NostrEvent | null> {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url);
    let newest: NostrEvent | null = null;
    let opened = false;
    let done = false;
    const finish = (err?: Error) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try { ws.close(); } catch { /* already closed */ }
      if (err) reject(err);
      else resolve(newest);
    };
    const timer = setTimeout(
      () => finish(opened ? undefined : new Error(`${url}: timeout`)),
      timeoutMs,
    );
    ws.onopen = () => {
      opened = true;
      ws.send(JSON.stringify(["REQ", "sub", filter]));
    };
    ws.onmessage = (m) => {
      let msg: unknown[];
      try { msg = JSON.parse(String(m.data)); } catch { return; }
      if (msg[0] === "EVENT" && msg[1] === "sub") {
        const ev = msg[2] as NostrEvent;
        if (typeof ev?.created_at === "number" && (!newest || ev.created_at > newest.created_at))
          newest = ev;
      } else if (msg[0] === "EOSE" || msg[0] === "CLOSED") {
        finish();
      }
    };
    ws.onerror = () => finish(new Error(`${url}: connection failed`));
    ws.onclose = () => finish(opened ? undefined : new Error(`${url}: closed`));
  });
}

/**
 * Query several relays in parallel; resolve with the first event that comes
 * back (null if every relay answered but had nothing), reject only if no
 * relay was reachable at all.
 */
/**
 * Query every relay, wait for them all (each bounded by timeoutMs), and return
 * the single newest event by created_at. Unlike fetchLatestEvent this never
 * settles for the first answer, so a lagging relay can't hand back a stale
 * copy. Resolves null if every reachable relay had nothing.
 */
export function fetchNewestEvent(
  relays: string[],
  filter: Filter,
  timeoutMs = 5000,
): Promise<NostrEvent | null> {
  return Promise.allSettled(
    relays.map((url) => queryRelay(url, filter, timeoutMs)),
  ).then((results) => {
    let newest: NostrEvent | null = null;
    for (const r of results)
      if (r.status === "fulfilled" && r.value && (!newest || r.value.created_at > newest.created_at))
        newest = r.value;
    return newest;
  });
}

export function fetchLatestEvent(
  relays: string[],
  filter: Filter,
  timeoutMs = 5000,
): Promise<NostrEvent | null> {
  return new Promise((resolve, reject) => {
    let pending = relays.length;
    let reached = false;
    let done = false;
    if (!pending) {
      resolve(null);
      return;
    }
    for (const url of relays)
      queryRelay(url, filter, timeoutMs).then(
        (ev) => {
          reached = true;
          if (done) return;
          if (ev) {
            done = true;
            resolve(ev);
          } else if (--pending === 0) {
            done = true;
            resolve(null);
          }
        },
        () => {
          if (done) return;
          if (--pending === 0) {
            done = true;
            if (reached) resolve(null);
            else reject(new Error("no relay reachable"));
          }
        },
      );
  });
}
